import { freshModal } from "../freshModal";
import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import axios from "../../axios/AxiosInstance";
import { documentsList } from "../../data/documentsList";
import { habilitationsList } from "../../data/habilitationsList";
import { arretMaladieTypes } from "../../data/arretMaladieTypes";
import { cacesOptions } from "../../data/cacesList";

const emptyFields = {
  startDate: "",
  endDate: "",
  deliveryDate: "",
  expirationDate: "",
  accidentDate: "",
  cardNumber: "",
  agreementAskDate: "",
  agreementAmount: "",
  deliveryCountry: "",
  inspectionDate: "",
  inspectedNumber: "",
  subType: "",
  payRollDate: "",
  vaccinType: "",
  level: "",
  hourlyRate: "",
  habilitations: "",
};

// Comme dans le dashboard : les dates sont fixées à midi (et 23h59 pour les
// dates de fin) pour éviter les décalages de fuseau horaire
const toMidday = (value) => {
  const date = new Date(value);
  date.setHours(12, 0, 0, 0);
  return date;
};

const toEndOfDay = (value) => {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const inputClass =
  "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent";

const DateField = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input lang="fr-FR"
      type="date"
      name={name}
      value={value}
      onChange={onChange}
      className={inputClass}
    />
  </div>
);

const TextField = ({ label, name, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={inputClass}
    />
  </div>
);

const AddDocumentModalFormSession = ({
  isOpen,
  onClose,
  candidate,
  candidateId,
  documents,
  setDocuments,
  onSubmit,
}) => {
  const [type, setType] = useState("");
  const [file, setFile] = useState(null);
  const [fields, setFields] = useState(emptyFields);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setType("");
      setFile(null);
      setFields(emptyFields);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setFields(emptyFields);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const form = e.currentTarget;
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("type", type);
      payload.append("candidateId", candidate?._id || candidateId);

      // Mêmes champs et même format que le dashboard jminterim
      const dateFields = {
        startDate: fields.startDate ? toMidday(fields.startDate) : null,
        endDate: fields.endDate
          ? type === "Atradius"
            ? toMidday(fields.endDate)
            : toEndOfDay(fields.endDate)
          : null,
        deliveryDate: fields.deliveryDate ? toMidday(fields.deliveryDate) : null,
        expirationDate: fields.expirationDate
          ? type === "Attestation d'honorabilité" ? toEndOfDay(fields.expirationDate) : toMidday(fields.expirationDate)
          : null,
        accidentDate: fields.accidentDate ? toMidday(fields.accidentDate) : null,
        agreementAskDate: fields.agreementAskDate
          ? toMidday(fields.agreementAskDate)
          : null,
        inspectionDate: fields.inspectionDate
          ? toMidday(fields.inspectionDate)
          : null,
        payRollDate: fields.payRollDate ? toMidday(fields.payRollDate) : null,
      };

      const textFields = {
        cardNumber: fields.cardNumber,
        agreementAmount: fields.agreementAmount,
        deliveryCountry: fields.deliveryCountry,
        inspectedNumber: fields.inspectedNumber,
        subType:
          type === "Habilitation" ? fields.habilitations : fields.subType,
        vaccinType: fields.vaccinType,
        level: fields.level,
        hourlyRate: fields.hourlyRate,
      };

      Object.entries({ ...dateFields, ...textFields }).forEach(
        ([name, value]) => {
          if (value) payload.append(name, value);
        },
      );

      const res = await axios.post("/document", payload);
      setType("");
      setFields({ ...emptyFields });
      setFile(null);
      form.reset();
      setDocuments?.((prev) => [...prev, res.data]);
      onSubmit?.(res.data);
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'envoi du document :", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const identityLikeTypes = {
    "Carte d'identité": "Numéro de la carte",
    Passeport: "Numéro du passeport",
    "Permis de conduire": "Numéro du permis",
    "Autorisation provisoire de séjour": "Numéro de la carte",
    "Carte de résident": "Numéro de la carte",
  };

  return (
    <Modal
      compact
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter un document"
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {type === 'Contrôle préfecture' && <p className="rounded-lg bg-blue-50 p-3 text-sm">Pour rattacher le contrôle au bon titre, utilisez Documents périmés → Titres de séjour / Préfecture → Enregistrer le contrôle. Un dépôt seul ne valide pas automatiquement un titre.</p>}

        {['Carte de séjour','Autorisation provisoire de séjour','Carte de résident','Titre de séjour'].includes(type) && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Après l’ajout de ce titre, un nouveau contrôle préfecture avec justificatif devra être enregistré dans Documents périmés → Titres de séjour / Préfecture.</p>}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de document*
          </label>
          <select
            name="type"
            value={type}
            onChange={handleTypeChange}
            required
            className={inputClass}
          >
            <option value="">Sélectionnez un type de document</option>
            {documentsList.map((doc) => (
              <option key={doc} value={doc}>
                {doc}
              </option>
            ))}
          </select>
        </div>

        {type === "Attestation d'honorabilité" && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md bg-blue-50 p-4">
          <DateField label="Début de validité" name="startDate" value={fields.startDate} onChange={handleFieldChange} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Fin de validité</label><input lang="fr-FR" type="date" name="expirationDate" value={fields.expirationDate} min={fields.startDate || undefined} onChange={handleFieldChange} className={inputClass} /></div>
        </div>}

        {/* Carte d'identité / Passeport / Permis / APS / Carte de résident */}
        {identityLikeTypes[type] && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de délivrance"
                name="deliveryDate"
                value={fields.deliveryDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date d'expiration"
                name="expirationDate"
                value={fields.expirationDate}
                onChange={handleFieldChange}
              />
            </div>
            <TextField
              label={identityLikeTypes[type]}
              name="cardNumber"
              value={fields.cardNumber}
              onChange={handleFieldChange}
              placeholder="Ex: 12AB34567"
            />
          </div>
        )}

        {(type === "Accident de travail" || type === "Accident de trajet") && (
          <div className="p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de l'accident"
              name="accidentDate"
              value={fields.accidentDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Arret maladie" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'arrêt
              </label>
              <select
                name="subType"
                value={fields.subType}
                onChange={handleFieldChange}
                className={inputClass}
              >
                <option value="">Sélectionnez le type d'arrêt</option>
                {arretMaladieTypes.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de début"
                name="startDate"
                value={fields.startDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date de fin"
                name="endDate"
                value={fields.endDate}
                onChange={handleFieldChange}
              />
            </div>
          </div>
        )}

        {type === "Atradius" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de la demande"
              name="agreementAskDate"
              value={fields.agreementAskDate}
              onChange={handleFieldChange}
            />
            <TextField
              label="Montant de l'accord"
              name="agreementAmount"
              value={fields.agreementAmount}
              onChange={handleFieldChange}
              placeholder="Ex: 5000"
            />
            <DateField
              label="Date de fin de l'accord"
              name="endDate"
              value={fields.endDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Habilitation" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'habilitation
              </label>
              <select
                name="habilitations"
                value={fields.habilitations}
                onChange={handleFieldChange}
                className={inputClass}
              >
                <option value="">Sélectionnez le type d'habilitation</option>
                {habilitationsList.map((habilitation) => (
                  <option key={habilitation} value={habilitation}>
                    {habilitation}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de début (facultatif)"
                name="startDate"
                value={fields.startDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date de fin (facultatif)"
                name="endDate"
                value={fields.endDate}
                onChange={handleFieldChange}
              />
            </div>
          </div>
        )}

        {type === "Carte de séjour" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de début"
                name="startDate"
                value={fields.startDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date d'expiration"
                name="expirationDate"
                value={fields.expirationDate}
                onChange={handleFieldChange}
              />
            </div>
            <TextField
              label="Numéro de la carte"
              name="cardNumber"
              value={fields.cardNumber}
              onChange={handleFieldChange}
              placeholder="Ex: 12AB34567"
            />
          </div>
        )}

        {type === "CACES" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de CACES
              </label>
              <select
                name="subType"
                value={fields.subType}
                onChange={handleFieldChange}
                className={inputClass}
              >
                <option value="">Sélectionnez le type de CACES</option>
                {cacesOptions.map((caces) => (
                  <option key={caces.value} value={caces.value}>
                    {caces.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de délivrance"
                name="deliveryDate"
                value={fields.deliveryDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date d'expiration"
                name="expirationDate"
                value={fields.expirationDate}
                onChange={handleFieldChange}
              />
            </div>
          </div>
        )}

        {type === "Carte BTP" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de début"
              name="startDate"
              value={fields.startDate}
              onChange={handleFieldChange}
            />
            <DateField
              label="Date de fin"
              name="endDate"
              value={fields.endDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {(type === "Carte conducteur" || type === "FCO" || type === "FIMO") && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de début"
                name="startDate"
                value={fields.startDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date de fin"
                name="endDate"
                value={fields.endDate}
                onChange={handleFieldChange}
              />
            </div>
            <TextField
              label={type === "Carte conducteur" ? "Numéro de la carte" : "Numéro"}
              name="cardNumber"
              value={fields.cardNumber}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Carte européenne" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateField
                label="Date de délivrance"
                name="deliveryDate"
                value={fields.deliveryDate}
                onChange={handleFieldChange}
              />
              <DateField
                label="Date d'expiration"
                name="expirationDate"
                value={fields.expirationDate}
                onChange={handleFieldChange}
              />
            </div>
            <TextField
              label="Numéro de la carte"
              name="cardNumber"
              value={fields.cardNumber}
              onChange={handleFieldChange}
            />
            <TextField
              label="Pays de délivrance"
              name="deliveryCountry"
              value={fields.deliveryCountry}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Contrôle préfecture" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date du contrôle"
              name="inspectionDate"
              value={fields.inspectionDate}
              onChange={handleFieldChange}
            />
            <TextField
              label="Numéro de la pièce contrôlée"
              name="inspectedNumber"
              value={fields.inspectedNumber}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {(type === "Document client" || type === "Autre") && (
          <div className="p-4 bg-blue-50 rounded-md">
            <TextField
              label="Type de document"
              name="subType"
              value={fields.subType}
              onChange={handleFieldChange}
              placeholder="Précisez le type de document"
            />
          </div>
        )}

        {type === "Fiche de paie" && (
          <div className="p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de la fiche de paie"
              name="payRollDate"
              value={fields.payRollDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Fiche de paie (controle taux et niveaux)" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de la fiche de paie"
              name="payRollDate"
              value={fields.payRollDate}
              onChange={handleFieldChange}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Niveau"
                name="level"
                value={fields.level}
                onChange={handleFieldChange}
              />
              <TextField
                label="Taux horaire"
                name="hourlyRate"
                value={fields.hourlyRate}
                onChange={handleFieldChange}
              />
            </div>
          </div>
        )}

        {type === "Vaccination" && (
          <div className="p-4 bg-blue-50 rounded-md">
            <TextField
              label="Type de vaccin"
              name="vaccinType"
              value={fields.vaccinType}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Visite médicale" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de la visite"
              name="startDate"
              value={fields.startDate}
              onChange={handleFieldChange}
            />
            <DateField
              label="Date de fin"
              name="endDate"
              value={fields.endDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        {type === "Travailleur handicapé ( RQTH )" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-md">
            <DateField
              label="Date de début"
              name="startDate"
              value={fields.startDate}
              onChange={handleFieldChange}
            />
            <DateField
              label="Date de fin"
              name="endDate"
              value={fields.endDate}
              onChange={handleFieldChange}
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fichier*
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                >
                  <span>Télécharger un fichier</span>
                  <input
                    id="file-upload"
                    name="file"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                    required
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, PNG, JPG, DOCX jusqu'à 10MB
              </p>
              {file && (
                <p className="text-sm text-green-600 mt-2">
                  Fichier sélectionné : {file.name}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            {isSubmitting ? "Envoi en cours..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const AddDocumentModal = freshModal(AddDocumentModalFormSession, "isOpen");
